import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  MenuList,
  Paper,
  Select,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import VendorFilterPaper from "../../../Shared/VendorFilterPaper";
import { CATALOG_VISIBILITY_FILTERS } from "./productCatalogStatus";
import {
  catalogFilterLabelSx,
  catalogFilterSearchInputSx,
  catalogFilterSelectSx,
  catalogFilterSliderSx,
  catalogSuggestPaperSx,
} from "./productCatalogFilterStyles";

export default function ProductCatalogFilters({
  searchWrapRef,
  searchInput,
  onSearchInputChange,
  onSearchFocus,
  onSearchKeyDown,
  onSearchSubmit,
  onSearchReset,
  searchPending,
  searchCommitted,
  suggestLoading,
  suggestOpen,
  suggestMinChars,
  suggestOptions,
  onSuggestSelect,
  sortOrder,
  onSortOrderChange,
  visibilityFilter,
  onVisibilityFilterChange,
  priceRange,
  onPriceRangeChange,
  onPriceRangeCommit,
  sliderExtent,
  sliderDisabled,
  isLoading,
}) {
  const showSearchButton = searchPending && !suggestLoading;
  const showClearButton = searchCommitted && !suggestLoading;

  return (
    <VendorFilterPaper sx={{ mb: 0 }}>
      <Grid2 container spacing={2} alignItems="center">
        <Grid2 size={{ xs: 12, md: 12 }}>
          <Box ref={searchWrapRef} sx={{ position: "relative", width: "100%" }}>
            <TextField
              fullWidth
              placeholder="Cerca prodotto, modello, brand..."
              value={searchInput}
              onChange={onSearchInputChange}
              onFocus={onSearchFocus}
              onKeyDown={onSearchKeyDown}
              slotProps={{
                input: {
                  endAdornment: suggestLoading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ) : showSearchButton ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={onSearchSubmit}
                        aria-label="Cerca"
                      >
                        <SearchIcon sx={{ color: "#0b3d87", fontSize: 20 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : showClearButton ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={onSearchReset}
                        aria-label="Cancella ricerca"
                      >
                        <CloseIcon sx={{ color: "#64748b", fontSize: 20 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  sx: catalogFilterSearchInputSx,
                },
              }}
            />
            {Boolean(suggestOpen && searchInput.trim().length >= suggestMinChars && suggestOptions.length > 0) && (
              <Paper elevation={0} sx={catalogSuggestPaperSx}>
                <MenuList dense disablePadding>
                  {suggestOptions.map((p) => (
                    <MenuItem
                      key={p.catalogListId ?? p.productListId ?? p.nome}
                      sx={{
                        fontFamily: "Rubik",
                        fontSize: "0.9rem",
                        py: 1,
                        "&:hover": { backgroundColor: "#f1f5f9" },
                      }}
                      onClick={() => onSuggestSelect(p)}
                    >
                      {p.nome}
                    </MenuItem>
                  ))}
                </MenuList>
              </Paper>
            )}
          </Box>
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <Typography sx={catalogFilterLabelSx}>Ordina</Typography>
          <Select
            fullWidth
            size="small"
            value={sortOrder === "desc" ? "price-desc" : "price-asc"}
            onChange={(e) => onSortOrderChange(e.target.value === "price-desc" ? "desc" : "asc")}
            sx={catalogFilterSelectSx}
          >
            <MenuItem value="price-asc">Prezzo crescente</MenuItem>
            <MenuItem value="price-desc">Prezzo decrescente</MenuItem>
          </Select>
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <Typography sx={catalogFilterLabelSx}>Visibilità</Typography>
          <Select
            fullWidth
            size="small"
            value={visibilityFilter}
            onChange={(e) => onVisibilityFilterChange(e.target.value)}
            sx={catalogFilterSelectSx}
          >
            <MenuItem value={CATALOG_VISIBILITY_FILTERS.ALL}>Tutti i prodotti</MenuItem>
            <MenuItem value={CATALOG_VISIBILITY_FILTERS.DEACTIVATED}>Solo disattivati</MenuItem>
            <MenuItem value={CATALOG_VISIBILITY_FILTERS.DELETED}>Solo eliminati</MenuItem>
          </Select>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 4 }}>
          <Typography
            sx={catalogFilterLabelSx}
            title={`Fascia ${Number(priceRange[0]).toFixed(2)}€ - ${Number(priceRange[1]).toFixed(2)}€`}
          >
            Prezzo {Number(priceRange[0]).toFixed(2)}€ – {Number(priceRange[1]).toFixed(2)}€
          </Typography>
          <Box sx={{ px: 0.5, pt: 0.5 }}>
            <Slider
              value={priceRange}
              onChange={(_, v) => onPriceRangeChange(v)}
              onChangeCommitted={(_, v) => onPriceRangeCommit(v)}
              min={sliderExtent.min}
              max={sliderExtent.max}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${Number(v).toFixed(2)}€`}
              disabled={sliderDisabled || isLoading}
              sx={catalogFilterSliderSx}
            />
          </Box>
        </Grid2>
      </Grid2>
    </VendorFilterPaper>
  );
}

ProductCatalogFilters.propTypes = {
  searchWrapRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  searchInput: PropTypes.string.isRequired,
  onSearchInputChange: PropTypes.func.isRequired,
  onSearchFocus: PropTypes.func.isRequired,
  onSearchKeyDown: PropTypes.func.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,
  onSearchReset: PropTypes.func.isRequired,
  searchPending: PropTypes.bool.isRequired,
  searchCommitted: PropTypes.bool.isRequired,
  suggestLoading: PropTypes.bool.isRequired,
  suggestOpen: PropTypes.bool.isRequired,
  suggestMinChars: PropTypes.number.isRequired,
  suggestOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSuggestSelect: PropTypes.func.isRequired,
  sortOrder: PropTypes.string.isRequired,
  onSortOrderChange: PropTypes.func.isRequired,
  visibilityFilter: PropTypes.string.isRequired,
  onVisibilityFilterChange: PropTypes.func.isRequired,
  priceRange: PropTypes.arrayOf(PropTypes.number).isRequired,
  onPriceRangeChange: PropTypes.func.isRequired,
  onPriceRangeCommit: PropTypes.func.isRequired,
  sliderExtent: PropTypes.shape({
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
  }).isRequired,
  sliderDisabled: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
};
